<script lang="ts">
  import { onDestroy } from 'svelte';

  import { ApiError } from '@/teoCore/errors/ApiError';
  import { TeoError } from '@/teoCore/errors/TeoError';
  import type {
    AiSuggestionV2Request,
    APITeoMarker,
    SuggestionPreset,
    SuggestionV2Context,
  } from '@/teoCore/TeoApi';
  import type { TeoText } from '@/teoCore/text/TeoText';
  import CtaButton from '@/ui/elements/buttons/CTAButton.svelte';
  import DropDown from '@/ui/elements/dropdown/DropDown.svelte';
  import type { DropDownItem } from '@/ui/elements/dropdown/DropDown/DropDownSelect.svelte';
  import { useActiveText, useTeoCore } from '@/utils/teo-context';

  type MarkerMode = 'none' | 'paragraph' | 'sentence' | 'auto';
  type MarkerClickMode = Exclude<MarkerMode, 'none' | 'auto'>;

  interface MarkerOwnership {
    text: TeoText;
    mode: MarkerClickMode;
    visibleMarkers: string[];
    clickableMarkers: string[];
    focusedMarkers: string[];
    removeClickListener: () => void;
  }

  interface SuggestionHistoryEntry {
    id: number;
    instruction: string;
    preset?: SuggestionPreset;
    markerMode: MarkerMode;
    markerId?: string;
    contextText: string;
    contextFocus?: string | null;
    submittedAt: number;
    appliedCount?: number;
    suggestions: Array<{ id: string; text: string }>;
  }

  const markerItems: DropDownItem<MarkerMode>[] = [
    { label: 'Auto-Modus (ein Schritt)', value: 'auto' },
    { label: 'Kein Marker (gesamter Text)', value: 'none' },
    { label: 'Absatz', value: 'paragraph' },
    { label: 'Satz', value: 'sentence' },
  ];

  const presetItems: DropDownItem<SuggestionPreset | ''>[] = [
    { label: 'Kein Preset', value: '' },
    // { label: 'Verständlichkeit', value: 'suggestions_vi' },
    // { label: 'Abstraktheit', value: 'suggestions_ai' },
    { label: 'Mehr persönliche Ansprache', value: 'suggestions_hi' },
    { label: 'Stärker auffordern', value: 'suggestions_ii_low' },
    { label: 'Weniger auffordern', value: 'suggestions_ii_high' },
    { label: 'Sätze kürzen', value: 'suggestions_lsentence' },
    { label: 'Abstrakte Substantive reduzieren', value: 'suggestions_abstract' },
    { label: 'Substantive reduzieren', value: 'suggestions_noun' },
    { label: 'Wörter kürzen', value: 'suggestions_long_word' },
    // { label: 'Passiv', value: 'suggestions_passive' },
    // { label: 'Einfache Sprache', value: 'translate_plain_language' },
  ];

  const core = useTeoCore();
  const activeText = useActiveText();

  let instruction = $state('');
  let selectedPreset: SuggestionPreset | '' = $state('');
  let markerMode: MarkerMode = $state('auto');
  let loading = $state(false);
  let errorMessage = $state<string | null>(null);
  let history = $state.raw<SuggestionHistoryEntry[]>([]);
  let nextHistoryId = 0;
  let markerOwnership: MarkerOwnership | null = null;

  const removeActiveTextListener = core.events.addEventListener('activeTextChanged', (text) => {
    releaseMarkerOwnership();
    if (markerMode === 'paragraph' || markerMode === 'sentence') {
      takeMarkerOwnership(text, markerMode);
    }
  });

  onDestroy(() => {
    removeActiveTextListener();
    releaseMarkerOwnership();
  });

  function takeMarkerOwnership(text: TeoText, mode: MarkerClickMode): void {
    if (text.ai.selection.isEnabled) {
      text.ai.selection.disable();
    }

    const ownership: MarkerOwnership = {
      text,
      mode: mode,
      visibleMarkers: [...text.ui.state.markers],
      clickableMarkers: [...text.markers.clickableMarkers],
      focusedMarkers: [...text.markers.focusedMarkers],
      removeClickListener: () => {},
    };

    ownership.removeClickListener = text.markers.events.addEventListener(
      'markerClicked',
      (detail) => {
        void handleMarkerClick(ownership, detail);
      },
    );
    markerOwnership = ownership;
    text.markers.setMarkers([...ownership.visibleMarkers, mode], [mode]);
  }

  function releaseMarkerOwnership(): void {
    const ownership = markerOwnership;
    if (!ownership) return;

    markerOwnership = null;
    ownership.removeClickListener();
    ownership.text.markers.setMarkers(ownership.visibleMarkers, ownership.clickableMarkers);
    ownership.text.markers.focusMarkers(ownership.focusedMarkers);
  }

  function selectMarkerMode(mode: MarkerMode): void {
    releaseMarkerOwnership();
    markerMode = mode;
    errorMessage = null;
    if (mode === 'paragraph' || mode === 'sentence') {
      takeMarkerOwnership(core.activeText, mode);
    }
  }

  async function handleMarkerClick(
    ownership: MarkerOwnership,
    detail: { markerId: string; marker: APITeoMarker },
  ): Promise<void> {
    const markerMatchesMode =
      ownership.mode === 'paragraph' ?
        detail.marker.marker.startsWith('paragraph')
      : detail.marker.marker === 'sentence';

    if (
      loading ||
      markerOwnership !== ownership ||
      core.activeText !== ownership.text ||
      markerMode !== ownership.mode ||
      !markerMatchesMode
    ) {
      return;
    }

    const analysis = ownership.text.analysis;
    if (!analysis) {
      showError(new Error('Keine aktuelle Analyse verfügbar.'));
      return;
    }

    ownership.text.markers.focusMarkers([detail.markerId]);
    const context = ownership.text.markers.createMarkerContext(detail.markerId);
    if (!context) {
      showError(new Error('Kein Marker-Kontext im Editor verfügbar.'));
      return;
    }
    await sendRequest(ownership.text, context, ownership.mode, detail.markerId);
  }

  async function handleSubmit(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    if (loading) return;
    if (markerMode === 'auto') {
      await runAutoStep(activeText.current);
      return;
    }
    if (markerMode !== 'none') return;

    const text = activeText.current;
    await sendRequest(text, { text: text.text }, 'none');
  }

  async function sendRequest(
    text: TeoText,
    context: SuggestionV2Context,
    submittedMarkerMode: MarkerMode,
    markerId?: string,
  ): Promise<void> {
    if (loading) return;

    try {
      const trimmedInstruction = instruction.trim();
      if (!trimmedInstruction && !selectedPreset) {
        throw new Error('Bitte einen User Prompt oder ein Preset wählen.');
      }
      if (!text.analysis) {
        throw new Error('Keine aktuelle Analyse verfügbar.');
      }

      const evaluationProfileId = core.dynamicConfig.options.evaluationProfileId;
      if (!evaluationProfileId) {
        throw new Error('Kein Evaluierungsprofil ausgewählt.');
      }
      if (!text.state.currentTemplateId) {
        throw new Error('Keine Konfiguration ausgewählt.');
      }
      if (!text.state.allPromptOptionsSelected) {
        throw new Error('Die Textkonfiguration ist nicht vollständig.');
      }

      const request: AiSuggestionV2Request = {
        context,
        evaluation_profile_id: evaluationProfileId,
        template_id: text.state.currentTemplateId,
        selected_options: text.state.selectedPromptOptions as Record<string, string>,
        use_whitelist: text.whiteListActive,
        ...(selectedPreset ? { preset: selectedPreset } : {}),
        ...(trimmedInstruction ? { instruction: trimmedInstruction } : {}),
      };

      errorMessage = null;
      loading = true;
      const response = await text.core.api.getAiSuggestionV2(request);
      if (response instanceof ApiError || TeoError.isError(response)) {
        throw response;
      }

      const entryId = ++nextHistoryId;
      history = [
        {
          id: entryId,
          instruction: trimmedInstruction,
          preset: selectedPreset || undefined,
          markerMode: submittedMarkerMode,
          markerId,
          contextText: context.text,
          contextFocus: context.textFocus,
          submittedAt: Date.now(),
          suggestions: response.suggestions.map((suggestion, index) => ({
            id: `${entryId}-${index}`,
            text: suggestion.text,
          })),
        },
        ...history,
      ];
    } catch (error) {
      showError(error);
    } finally {
      loading = false;
    }
  }

  async function runAutoStep(text: TeoText): Promise<void> {
    if (loading) return;

    try {
      const trimmedInstruction = instruction.trim();
      if (!trimmedInstruction && !selectedPreset) {
        throw new Error('Bitte einen User Prompt angeben oder ein Preset wählen.');
      }
      if (text.ai.auto.isRunning) {
        throw new Error('Auto-Optimierung läuft bereits.');
      }
      if (!text.analysis) {
        throw new Error('Keine aktuelle Analyse verfügbar.');
      }
      if (!text.state.allPromptOptionsSelected || !text.state.currentTemplateId) {
        throw new Error('Die Textkonfiguration ist nicht vollständig.');
      }

      errorMessage = null;
      loading = true;
      const applied = await text.ai.auto.run({
        preset: selectedPreset || undefined,
        instruction: trimmedInstruction || undefined,
      });

      const entryId = ++nextHistoryId;
      history = [
        {
          id: entryId,
          instruction: trimmedInstruction,
          preset: selectedPreset || undefined,
          markerMode: 'auto',
          contextText: text.text,
          submittedAt: Date.now(),
          appliedCount: applied,
          suggestions: [],
        },
        ...history,
      ];
    } catch (error) {
      showError(error);
    } finally {
      loading = false;
    }
  }

  function showError(error: unknown): void {
    errorMessage = TeoError.toString(error);
    if (ApiError.matches(error) || TeoError.isTeoError(error)) {
      core.notifications.addError(error);
    }
  }
</script>

<form class="mt-2 flex flex-col gap-3" onsubmit={handleSubmit}>
  <label class="flex flex-col gap-1 text-sm">
    <span class="font-medium">User Prompt</span>
    <textarea
      class="border-c-light bg-c-bg text-c-text min-h-28 w-full resize-y rounded-md border p-2 text-sm"
      bind:value={instruction}
      maxlength="5000"
      placeholder="Wenn hier etwas steht, überschreibt es die Anweisung aus dem Preset. (Ohne Preset muss hier etwas stehen.)"
      disabled={loading}
      rows={5}
    ></textarea>
  </label>

  <div class="flex flex-col gap-1 text-sm">
    <span class="font-medium">Preset</span>
    <DropDown
      class="w-full"
      items={presetItems}
      selected={selectedPreset}
      onItemClick={(preset) => (selectedPreset = preset)}
      ariaLabel="Preset"
    />
  </div>

  <div class="flex flex-col gap-1 text-sm">
    <span class="font-medium">Kontextauswahl</span>
    <DropDown
      class="w-full"
      items={markerItems}
      selected={markerMode}
      onItemClick={selectMarkerMode}
      ariaLabel="Kontextauswahl"
    />
  </div>

  {#if markerMode === 'none'}
    <p class="text-xs text-c-light">Beim Absenden wird der vollständige aktive Text verwendet.</p>
    <CtaButton type="submit" variant="primary" {loading}>Anfrage senden</CtaButton>
  {:else if markerMode === 'auto'}
    <p class="text-xs text-c-light">
      Ein Auto-Optimierungs-Schritt: die KI wählt die Absätze selbst und wendet die Vorschläge
      direkt an.
    </p>
    <CtaButton type="submit" variant="primary" {loading}>Schritt ausführen</CtaButton>
  {:else}
    <p class="text-xs text-c-light">
      Klicke einen {markerMode === 'paragraph' ? 'Absatz' : 'Satz'} im Text an. Die Anfrage wird direkt
      gesendet.
    </p>
  {/if}

  {#if loading}
    <p class="text-sm text-c-light" role="status">Lade Vorschlag...</p>
  {/if}

  {#if errorMessage}
    <div class="bg-red-100 text-red-800 rounded-md p-2 text-sm">
      <strong>Fehler:</strong>
      {errorMessage}
    </div>
  {/if}
</form>

<div class="mt-4 flex flex-col gap-3">
  <strong>Vorschläge</strong>
  {#if history.length === 0}
    <div class="bg-c-bg text-c-text rounded-md p-2 text-sm">Noch keine Antworten vorhanden.</div>
  {/if}

  {#each history as entry (entry.id)}
    <article class="bg-c-bg text-c-text flex flex-col gap-2 rounded-md p-3 text-sm">
      <div>
        {#if entry.instruction}
          <strong>User Prompt:</strong>
          {entry.instruction}
        {:else}
          <strong>User Prompt:</strong>
          <span class="text-c-light">Preset</span>
        {/if}
      </div>
      {#if entry.preset}
        <div class="text-xs text-c-light">Preset: {entry.preset}</div>
      {/if}
      <div class="text-xs text-c-light">
        {new Date(entry.submittedAt).toLocaleString()} ·
        {#if entry.markerMode === 'none'}
          Gesamter Text ({entry.contextText.length} Zeichen)
        {:else if entry.markerMode === 'auto'}
          Auto-Modus · {entry.appliedCount ?? 0} Absätze ersetzt
        {:else}
          {entry.markerMode === 'paragraph' ? 'Absatz' : 'Satz'} · Marker {entry.markerId}
          ({entry.contextText.length} Zeichen)
        {/if}
      </div>
      {#if entry.contextFocus}
        <div class="text-xs"><strong>Fokus:</strong> {entry.contextFocus}</div>
      {/if}
      {#if entry.markerMode === 'auto'}
        <!-- applied to the editor; there is nothing left to list -->
      {:else if entry.suggestions.length === 0}
        <div class="text-c-light">Die Antwort enthält keine Vorschläge.</div>
      {:else}
        {#each entry.suggestions as suggestion (suggestion.id)}
          <div class="border-c-light rounded-md border p-2">{suggestion.text}</div>
        {/each}
      {/if}
    </article>
  {/each}
</div>
